<template>
    <q-page padding>
        <!-- Modern Table Card -->
        <q-card class="ingenierias-table-card">
            <q-table ref="tableRef" :rows="rows" :columns="columns" row-key="name" v-model:pagination="pagination"
                :loading="loading" :filter="filter" @request="onRequest"
                class="modern-ingenierias-table sticky-header-table" dense flat>
                <template v-slot:top-left>
                    <div class="page-title">
                        <q-icon name="construction" class="q-mr-md" size="lg" />
                        <div>
                            <div class="text-h6">Ingenierías</div>
                            <div class="text-caption text-grey-7">Ingeniería de Cámaras</div>
                        </div>
                    </div>
                </template>
                <template v-slot:top-right>
                    <div class="filters-section">
                        <q-input v-model="filter" placeholder="Buscar ingenierías..." class="search-input" outlined
                            dense debounce="300">
                            <template v-slot:prepend>
                                <q-icon name="search" />
                            </template>
                            <template v-slot:append>
                                <q-icon v-if="filter" name="close" @click="filter = ''" class="cursor-pointer" />
                            </template>
                        </q-input>
                    </div>
                </template>

                <template v-slot:body-cell-estado="props">
                    <q-td :props="props">
                        <div class="estado-cell">
                            <GltnSelectEstado v-model="props.row.nombre" :presupuesto="props.row.presupuesto_id"
                                disabled />
                        </div>
                    </q-td>
                </template>

                <template v-slot:body-cell-acciones="props">
                    <q-td :props="props">
                        <div class="action-buttons">
                            <q-btn v-if="props.row.estado_id === '6' || props.row.estado_id === '7'" color="primary"
                                round flat icon="upload" size="sm" @click="importar(props.row)" class="action-btn">
                                <q-tooltip>Importar ingeniería</q-tooltip>
                            </q-btn>
                            <q-btn v-if="props.row.estado_id !== '6'" color="secondary" round flat icon="visibility"
                                size="sm" class="action-btn" @click="show(props.row)">
                                <q-tooltip>Mostrar detalles</q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </template>
            </q-table>
        </q-card>

        <!-- Dialog de Importación -->
        <q-dialog v-model="dialog" persistent>
            <q-card class="import-dialog-card">
                <q-card-section class="bg-primary text-white">
                    <div class="text-h6 row items-center">
                        <q-icon name="engineering" size="32px" class="q-mr-sm" />
                        Importar Ingeniería
                    </div>
                </q-card-section>

                <q-card-section class="q-pt-md">
                    <q-file outlined v-model="importData" label="Seleccionar archivo Excel" clearable
                        accept=".xlsx,.xls" class="import-file-input">
                        <template v-slot:prepend>
                            <q-icon name="attach_file" color="primary" />
                        </template>
                        <template v-slot:hint>
                            Formatos soportados: .xlsx, .xls
                        </template>
                    </q-file>
                </q-card-section>

                <q-card-actions align="right" class="q-pa-md q-gutter-sm">
                    <q-btn outline label="Cancelar" color="negative" v-close-popup :disable="importando" no-caps />
                    <q-btn unelevated label="Importar" color="primary" @click="confirmImport" :loading="importando"
                        no-caps />
                </q-card-actions>

                <q-inner-loading :showing="importando">
                    <q-spinner-hourglass size="50px" color="primary" />
                </q-inner-loading>
            </q-card>
        </q-dialog>

        <prorrateo-ingenieria v-model="prorrateoIngenieriaDialog" :rows="datosIngenieria" :producto="productoIngenieria"
            :ingenieria-id="ingenieriaId" @updated="importUpdated" />
        <items-ingenieria v-model="previewIngenieriaDialog" :ingenieria-id="ingenieriaId" />
    </q-page>
</template>

<script setup>
import GltnSelectEstado from "@/components/form/GltnSelectEstado.vue";
import IngenieriaService from "@/services/IngenieriaService";
import ProrrateoIngenieria from "@/pages/Ingenierias/ProrrateoIngenieria.vue";
import ItemsIngenieria from "@/pages/Ingenierias/ItemsIngenieria.vue";
import { ref, onMounted } from "vue";
import { useQuasar } from "quasar";

const $q = useQuasar();
const datosIngenieria = ref(null);
const dialog = ref(false);
const filter = ref("");
const filters = ref({});
const importData = ref("");
const importando = ref(false);
const ingenieriaId = ref(null);
const loading = ref(false);
const prorrateoIngenieriaDialog = ref(false);
const previewIngenieriaDialog = ref(false);
const productoIngenieria = ref(null);
const rows = ref([]);
const tableRef = ref(null);
const pagination = ref({
    sortBy: "id",
    descending: true,
    page: 1,
    rowsPerPage: 20,
    rowsNumber: 10,
    cliente: true,
});
const columns = [
    { name: "id", label: "ID", field: "id", align: "left", sortable: true },
    {
        name: "referencia",
        label: "Obra",
        field: "referencia",
        align: "left",
        sortable: true,
    },
    {
        name: "cliente",
        label: "Cliente",
        field: "A1_NOME",
        align: "left",
        sortable: true,
    },
    {
        name: "presupuestoId",
        label: "Presupuesto",
        field: "presupuesto_id",
        align: "left",
        sortable: true,
    },
    {
        name: "presupuestoItemId",
        label: "Presupuesto Item",
        field: "presupuesto_item_id",
        align: "left",
        sortable: true,
    },
    {
        name: "estado",
        label: "Estado",
        field: "estado_id",
        align: "left",
        sortable: true,
    },
    {
        label: "Acciones",
        name: "acciones",
        align: "center",
    },
];

const onRequest = async (props) => {
    const filter = props.filter;
    const { page, rowsPerPage, sortBy, descending } = props.pagination;
    loading.value = true;
    try {
        const { data, per_page, current_page, total } =
            await IngenieriaService.grid({
                page,
                rowsPerPage,
                sortBy,
                descending,
                filter,
                filters: filters.value,
            });
        rows.value = data;
        pagination.value.rowsPerPage = per_page;
        pagination.value.page = current_page;
        pagination.value.rowsNumber = total;
        pagination.value.sortBy = sortBy;
        pagination.value.descending = descending;
    } catch (error) {
        $q.notify({
            type: "negative",
            message: "Error al cargar las ingenierias.",
        });
        console.error("Error loading ingenierias:", error);
    }
    loading.value = false;
};

onMounted(() => {
    tableRef.value.requestServerInteraction();
});
const importar = (row) => {
    dialog.value = true;
    ingenieriaId.value = row.id;
};
const importUpdated = () => {
    tableRef.value.requestServerInteraction();
    prorrateoIngenieriaDialog.value = false;
};
const confirmImport = async () => {
    if (!importData.value) {
        $q.notify({
            type: "warning",
            message: "Por favor, seleccione un archivo para importar.",
        });
        return;
    }
    importando.value = true;
    try {
        const { datos, producto } = await IngenieriaService.importar(ingenieriaId.value, importData.value);
        datosIngenieria.value = datos;
        productoIngenieria.value = producto;
        prorrateoIngenieriaDialog.value = true;
        dialog.value = false;
    } catch (error) {
        $q.notify({
            type: "negative",
            message: "Error al importar la ingeniería.",
        });
        console.error("Error importing ingeniería:", error);
    } finally {
        importando.value = false;
    }
};

const show = (row) => {
    ingenieriaId.value = row.id;
    previewIngenieriaDialog.value = true;
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

// Modern Table Card
.ingenierias-table-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .modern-ingenierias-table {
        :deep(.q-table__top) {
            padding: 16px;
            background: linear-gradient(to right, #f5f5f5, #fafafa);
            border-bottom: 2px solid #e0e0e0;
        }

        :deep(.q-table thead th) {
            font-weight: 600;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            background: #fafafa;
            color: #424242;
        }

        :deep(.q-table tbody tr) {
            transition: all 0.2s ease;

            &:hover {
                background: #f5f9ff;
                transform: translateX(2px);
            }
        }

        :deep(.q-table tbody td) {
            border-bottom: 1px solid #f0f0f0;
        }
    }
}

// Toolbar
.filters-section {
    display: flex;
    align-items: center;
    gap: 12px;

    .search-input {
        min-width: 300px;
    }
}

.toolbar-info {
    display: flex;
    align-items: center;
    gap: 8px;

    .q-chip {
        font-weight: 500;
    }
}

// Cell Styles
.estado-cell {
    display: flex;
    align-items: center;
}

.action-buttons {
    display: flex;
    gap: 4px;
    justify-content: center;

    .action-btn {
        transition: all 0.2s ease;

        &:hover {
            transform: scale(1.1);
        }
    }
}

// Import Dialog
.import-dialog-card {
    min-width: 400px;
    border-radius: 12px;
    overflow: hidden;

    .import-file-input {
        :deep(.q-field__control) {
            border-radius: 8px;
        }
    }
}

// Responsive
@media (max-width: 1024px) {
    .filters-section {
        .search-input {
            min-width: 200px;
        }
    }

    .import-dialog-card {
        min-width: 320px;
    }
}
</style>
